import React, { useState, useMemo, createContext, useContext, useEffect, useCallback } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import {
  CssBaseline, Box, useMediaQuery, Typography, Button, LinearProgress,
  Tooltip, Chip, Collapse, Snackbar, Alert, IconButton,
} from '@mui/material';
import VerifiedUser      from '@mui/icons-material/VerifiedUser';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import CheckCircle       from '@mui/icons-material/CheckCircle';
import SyncIcon          from '@mui/icons-material/Sync';
import ErrorOutline      from '@mui/icons-material/ErrorOutline';
import RateReview        from '@mui/icons-material/RateReview';
import CloseIcon         from '@mui/icons-material/Close';
import axios             from './api/client';
import { useUpload }     from './contexts/UploadContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import FileUpload from './components/FileUpload';
import Dashboard from './components/Dashboard';
import DocumentViewer from './components/DocumentViewer';
import Sidebar from './components/Navigation';
import AuditPage from './components/AuditPage';
import LineItemFilterPage from './components/LineItemFilterPage';
import GroupsPage from './components/GroupsPage';
import RevisionHistoryPage from './components/RevisionHistoryPage';
import UploadProgressWidget from './components/UploadProgressWidget';
import { UploadProvider } from './contexts/UploadContext';
import { BRProvider, useBR } from './contexts/BRContext';
import { AuthProvider, RequireAuth, RequireAdmin } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import AuthCallback from './components/AuthCallback';
import AuthError from './components/AuthError';
import ManageUsersPage from './components/ManageUsersPage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8004';

export const ThemeModeContext = createContext({ toggleThemeMode: () => {}, theme: 'dark' });

const queryClient = new QueryClient();

export const SIDEBAR_WIDTH = 240;
export const SIDEBAR_COLLAPSED_WIDTH = 72;

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: { main: '#4a4f5a', light: '#6b7280', dark: '#373c45', contrastText: '#ffffff' },
          secondary: { main: '#6b7280', light: '#9ca3af', dark: '#374151' },
          success: { main: '#16a34a', light: '#4ade80', dark: '#15803d' },
          warning: { main: '#d97706', light: '#fcd34d', dark: '#b45309' },
          error: { main: '#dc2626', light: '#fca5a5', dark: '#b91c1c' },
          background: { default: '#f5f5f5', paper: '#ffffff' },
          text: { primary: '#111111', secondary: '#6b7280' },
          divider: 'rgba(0,0,0,0.09)',
        }
      : {
          primary: { main: '#818cf8', light: '#a5b4fc', dark: '#6366f1', contrastText: '#fff' },
          secondary: { main: '#22d3ee', light: '#67e8f9', dark: '#06b6d4' },
          success: { main: '#34d399', light: '#6ee7b7', dark: '#10b981' },
          warning: { main: '#fbbf24', light: '#fcd34d', dark: '#f59e0b' },
          error: { main: '#f87171', light: '#fca5a5', dark: '#ef4444' },
          background: { default: '#070c18', paper: '#0d1526' },
          text: { primary: '#e2e8f0', secondary: '#94a3b8' },
          divider: 'rgba(148, 163, 184, 0.08)',
        }),
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontFamily: '"DM Sans", sans-serif', fontWeight: 700, letterSpacing: '-0.03em' },
    h2: { fontFamily: '"DM Sans", sans-serif', fontWeight: 700, letterSpacing: '-0.025em' },
    h3: { fontFamily: '"DM Sans", sans-serif', fontWeight: 700, letterSpacing: '-0.02em', fontSize: '1.875rem' },
    h4: { fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.018em' },
    h5: { fontFamily: '"DM Sans", sans-serif', fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '1.05rem', letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 500, letterSpacing: '-0.005em' },
    subtitle2: { fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' },
    body1: { letterSpacing: '-0.005em', fontSize: '0.875rem' },
    body2: { letterSpacing: '-0.005em', fontSize: '0.8125rem' },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: '-0.005em' },
    caption: { letterSpacing: '0.01em', fontSize: '0.75rem' },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          padding: '9px 18px',
          boxShadow: 'none',
          fontSize: '0.875rem',
          fontWeight: 600,
          transition: 'all 0.15s ease',
          '&:hover': { boxShadow: 'none', transform: 'translateY(-1px)' },
          '&:active': { transform: 'translateY(0)' },
        },
        containedPrimary: {
          background: mode === 'light'
            ? '#4a4f5a'
            : 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
          '&:hover': {
            background: mode === 'light'
              ? '#373c45'
              : 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
          },
        },
        sizeSmall: { padding: '6px 14px', fontSize: '0.8125rem' },
        sizeLarge: { padding: '12px 24px', fontSize: '0.9375rem' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: mode === 'light'
            ? '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)'
            : '0 1px 2px rgba(0,0,0,0.6), 0 4px 16px rgba(0,0,0,0.4)',
        },
        outlined: {
          boxShadow: 'none',
          borderColor: mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(148, 163, 184, 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: mode === 'light'
            ? '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.06)'
            : '0 1px 2px rgba(0,0,0,0.6), 0 4px 16px rgba(0,0,0,0.4)',
          border: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(148,163,184,0.08)'}`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(148,163,184,0.07)'}`,
          padding: '13px 16px',
          fontSize: '0.875rem',
        },
        head: {
          fontWeight: 600,
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: mode === 'light' ? '#6b7280' : '#94a3b8',
          backgroundColor: mode === 'light' ? '#f0f0f0' : '#09111f',
          padding: '11px 16px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.1s ease',
          '&:hover': {
            backgroundColor: mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(99,102,241,0.05)',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(148,163,184,0.08)'}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, backgroundImage: 'none' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 6, fontSize: '0.75rem' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            fontSize: '0.875rem',
            backgroundColor: mode === 'light' ? '#fff' : 'rgba(255,255,255,0.03)',
            transition: 'all 0.15s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'light' ? '#555555' : '#6366f1',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontSize: '0.875rem',
          color: mode === 'light' ? '#111111' : '#e2e8f0',
          backgroundColor: mode === 'light' ? '#ffffff' : 'rgba(255,255,255,0.04)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: mode === 'light' ? 'rgba(0,0,0,0.18)' : 'rgba(148,163,184,0.2)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: mode === 'light' ? '#333333' : '#6366f1',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: mode === 'light' ? '#111111' : '#818cf8',
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: mode === 'light' ? '#6b7280' : '#94a3b8',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          color: mode === 'light' ? '#6b7280' : '#94a3b8',
          '&.Mui-focused': {
            color: mode === 'light' ? '#111111' : '#818cf8',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          color: mode === 'light' ? '#111111' : '#e2e8f0',
          '&:hover': {
            backgroundColor: mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(99,102,241,0.1)',
          },
          '&.Mui-selected': {
            backgroundColor: mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(99,102,241,0.15)',
            color: mode === 'light' ? '#111111' : '#818cf8',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(99,102,241,0.2)',
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: '10px', fontSize: '0.875rem' } },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 4, height: 5 } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
          borderRadius: 6,
          backgroundColor: mode === 'light' ? '#1a1a1a' : '#1e293b',
          border: `1px solid ${mode === 'light' ? 'rgba(255,255,255,0.1)' : 'rgba(148,163,184,0.15)'}`,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          '&:before': { display: 'none' },
          borderRadius: '10px !important',
          overflow: 'hidden',
          border: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(148,163,184,0.08)'}`,
          boxShadow: 'none',
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: '0 20px',
          minHeight: '52px',
          '&.Mui-expanded': { minHeight: '52px' },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: mode === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(148,163,184,0.08)',
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: { root: { bottom: 24 } },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: mode === 'light' ? '#ffffff' : '#1a2540',
          border: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(148,163,184,0.15)'}`,
          boxShadow: mode === 'light'
            ? '0 8px 32px rgba(0,0,0,0.12)'
            : '0 8px 32px rgba(0,0,0,0.6)',
          borderRadius: '10px',
        },
      },
    },
  },
});

/** Persistent top bar — always visible, survives route changes. */
function AppHeader({ processingAlert, onDismissAlert }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const {
    syncing, syncDone, syncError, progress, stepLabel,
    brLatest, handleSync,
    verifyHandler, verifyPoId,
  } = useBR();

  // Format the last-sync timestamp in the user's local timezone.
  // The backend returns a naive UTC ISO string (no Z / +00:00).  Without an
  // explicit offset, JS Date() treats it as local time and skips conversion.
  // Appending '+00:00' tells JS it's UTC so toLocaleString() converts correctly.
  const syncTimeLabel = (() => {
    if (!brLatest?.has_data || !brLatest.extracted_at) return null;
    let raw = brLatest.extracted_at;
    // Ensure the string carries a UTC offset
    if (!raw.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(raw)) raw += '+00:00';
    return new Date(raw).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
      hour12: true,
    });
  })();

  // Only render the sticky wrapper when there's something to show
  const hasControls = true; // buttons always present
  const showBanner  = !!processingAlert;

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 200,
        // Transparent by default — only the pill and banners have their own backgrounds
        pointerEvents: 'none', // let clicks pass through the empty areas
      }}
    >
      {/* ── Compact controls pill — floats to the top-right ── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          px: { xs: 1.5, sm: 2 },
          pt: 1,
          pb: syncing ? 0.5 : 1,
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.5,
            borderRadius: '10px',
            backgroundColor: isDark
              ? 'rgba(10,17,32,0.88)'
              : 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${isDark ? 'rgba(148,163,184,0.12)' : 'rgba(0,0,0,0.1)'}`,
            boxShadow: isDark
              ? '0 2px 12px rgba(0,0,0,0.35)'
              : '0 2px 10px rgba(0,0,0,0.08)',
            pointerEvents: 'auto',
            flexWrap: 'wrap',
          }}
        >
          {/* Last sync timestamp */}
          {syncTimeLabel && !syncing && (
            <Tooltip title={`${brLatest.row_count} POs in latest export`} arrow>
              <Chip
                size="small"
                icon={<CheckCircleOutline sx={{ fontSize: '13px !important', color: syncDone ? '#10b981' : (isDark ? '#64748b' : '#94a3b8') }} />}
                label={`BR: ${syncTimeLabel}`}
                sx={{
                  fontSize: '0.68rem',
                  height: 22,
                  fontWeight: 500,
                  color: isDark ? '#94a3b8' : '#64748b',
                  backgroundColor: 'transparent',
                  border: 'none',
                  '& .MuiChip-icon': { ml: '4px' },
                }}
              />
            </Tooltip>
          )}

          {/* Sync error inline */}
          {syncError && !syncing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ErrorOutline sx={{ fontSize: 13, color: '#ef4444' }} />
              <Typography sx={{ fontSize: '0.68rem', color: '#ef4444', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {syncError}
              </Typography>
            </Box>
          )}

          {/* Sync BR button */}
          <Tooltip title={syncTimeLabel ? `Last synced: ${syncTimeLabel} · ${brLatest?.row_count} POs` : 'No BR data loaded yet'} arrow>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  syncing
                    ? <SyncIcon sx={{ fontSize: '13px !important', animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                    : syncDone
                      ? <CheckCircleOutline sx={{ fontSize: '13px !important' }} />
                      : <SyncIcon sx={{ fontSize: '13px !important' }} />
                }
                onClick={handleSync}
                disabled={syncing}
                sx={{
                  fontSize: '0.72rem',
                  height: 26,
                  px: 1.1,
                  py: 0,
                  minWidth: 0,
                  borderColor: syncDone
                    ? 'rgba(16,185,129,0.45)'
                    : isDark ? 'rgba(99,102,241,0.4)' : 'rgba(0,0,0,0.3)',
                  color: syncDone
                    ? '#10b981'
                    : isDark ? '#818cf8' : '#4f46e5',
                  '&:hover': {
                    borderColor: syncDone ? '#10b981' : '#6366f1',
                    bgcolor: syncDone ? 'rgba(16,185,129,0.06)' : 'rgba(99,102,241,0.06)',
                  },
                }}
              >
                {syncing ? 'Syncing…' : syncDone ? 'Synced ✓' : 'Sync BR'}
              </Button>
            </span>
          </Tooltip>

          {/* Verify BR — only shown when AuditPage is active */}
          {verifyPoId && verifyHandler && (
            <>
              {/* Thin divider between Sync and Verify */}
              <Box sx={{ width: '1px', height: 18, bgcolor: isDark ? 'rgba(148,163,184,0.15)' : 'rgba(15,23,42,0.1)', flexShrink: 0 }} />
              <Button
                size="small"
                variant="outlined"
                startIcon={<VerifiedUser sx={{ fontSize: '13px !important' }} />}
                onClick={verifyHandler}
                sx={{
                  fontSize: '0.72rem',
                  height: 26,
                  px: 1.1,
                  py: 0,
                  minWidth: 0,
                  borderColor: isDark ? 'rgba(245,158,11,0.4)' : 'rgba(217,119,6,0.35)',
                  color: isDark ? '#fbbf24' : '#d97706',
                  '&:hover': {
                    borderColor: '#f59e0b',
                    bgcolor: 'rgba(245,158,11,0.06)',
                  },
                }}
              >
                Verify BR
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* ── Progress bar — full-width, appears while syncing ── */}
      {syncing && (
        <Box
          sx={{
            mx: { xs: 1.5, sm: 2 },
            mb: 0.75,
            pointerEvents: 'none',
          }}
        >
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 3,
              borderRadius: 2,
              backgroundColor: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(0,0,0,0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 2,
                background: 'linear-gradient(90deg, #6366f1, #818cf8)',
                transition: 'transform 0.6s ease',
              },
            }}
          />
          <Typography sx={{ fontSize: '0.63rem', color: isDark ? '#64748b' : '#94a3b8', mt: 0.25 }}>
            {stepLabel} · {progress}%
          </Typography>
        </Box>
      )}

      {/* ── Processing-done notification banner — full-width ── */}
      <Collapse in={showBanner}>
        {processingAlert && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              mx: { xs: 1.5, sm: 2 },
              mb: 0.75,
              px: 1.5, py: 0.6,
              borderRadius: '10px',
              bgcolor: isDark ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.07)',
              border: `1px solid ${isDark ? 'rgba(16,185,129,0.2)' : 'rgba(16,185,129,0.15)'}`,
              pointerEvents: 'auto',
            }}
          >
            <CheckCircle sx={{ fontSize: 14, color: '#10b981', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#10b981', flex: 1 }}>
              {processingAlert.message}
            </Typography>
            {processingAlert.onAudit && (
              <Button
                size="small"
                startIcon={<RateReview sx={{ fontSize: '12px !important' }} />}
                onClick={processingAlert.onAudit}
                sx={{ fontSize: '0.7rem', py: 0.2, px: 0.9, color: '#10b981', borderColor: 'rgba(16,185,129,0.4)', minWidth: 0 }}
                variant="outlined"
              >
                {processingAlert.auditLabel || 'Audit now'}
              </Button>
            )}
            <CloseIcon
              sx={{ fontSize: 14, color: isDark ? '#64748b' : '#94a3b8', cursor: 'pointer', flexShrink: 0, ml: 0.5, '&:hover': { color: '#ef4444' } }}
              onClick={onDismissAlert}
            />
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

function AppLayoutInner() {
  const { toggleThemeMode, theme } = useContext(ThemeModeContext);
  const location  = useLocation();
  const navigate  = useNavigate();
  const isMobile  = useMediaQuery('(max-width:900px)');
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  // Processing-done banner state
  const [processingAlert, setProcessingAlert] = useState(null);
  const { jobs, completionNotice, clearCompletionNotice, toasts, dismissToast } = useUpload();

  /**
   * Navigate to batch audit.
   *
   * directPoList — array of { id, customer_po_number } from the just-finished
   *   job's result_po_ids.  When present we go straight to those POs without
   *   an extra round-trip.  When absent (e.g. the "Audit now" banner button is
   *   clicked after the fact) we fall back to fetching all review-status POs.
   */
  const openBatchAudit = useCallback(async (directPoList = null) => {
    try {
      let pos;
      if (directPoList && directPoList.length > 0) {
        // Use exactly the POs that were just processed — no extra fetch needed
        pos = directPoList; // [{id, customer_po_number, status?}]
      } else {
        // Fallback: user clicked "Audit now" banner — fetch both draft and review POs
        // (new POs start as 'draft'; mixed-batch new POs never become 'review' automatically)
        const [r1, r2] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/purchase-orders/`, {
            params: { status: 'review', sort_by: 'id', sort_order: 'desc', limit: 100 },
          }),
          axios.get(`${API_BASE_URL}/api/purchase-orders/`, {
            params: { status: 'draft', sort_by: 'id', sort_order: 'desc', limit: 100 },
          }),
        ]);
        pos = [...(r1.data?.data || []), ...(r2.data?.data || [])];
      }
      if (pos.length === 0) return;

      // Deduplicate by ID (same PO can appear in result_po_ids of multiple jobs)
      const seenIds = new Set();
      pos = pos.filter(p => { const k = String(p.id); if (seenIds.has(k)) return false; seenIds.add(k); return true; });

      const isReviewed = (p) => {
        const st = (p.status || '').toLowerCase();
        return st === 'accepted' || st === 'rejected';
      };
      const needsReview = pos.filter((p) => !isReviewed(p));
      const alreadyReviewed = pos.filter(isReviewed).map((p) => String(p.id));

      if (needsReview.length === 0) return;

      const batch = needsReview.map((p) => p.id);
      const poNumberMap = Object.fromEntries(
        needsReview.map((p) => [String(p.id), p.customer_po_number || String(p.id)])
      );
      navigate(`/audit/${batch[0]}`, {
        state: { processedPoIds: batch, poNumberMap, reviewedPoIds: alreadyReviewed },
      });
    } catch (e) {
      console.warn('[AppLayout] openBatchAudit error', e);
    }
  }, [navigate]);

  // When processing completes: smart auto-navigate + contextual notification.
  //
  // Three categories of settled jobs:
  //   "new"      — status=done, no revision_updates   → auto-navigate to batch audit
  //   "revision" — status=done, has revision_updates  → stay in widget; no auto-nav
  //   "dup-new"  — status=duplicate + saved ≥1 new PO → treat new POs like "new"
  //
  // Pure duplicate jobs (0 new POs saved) never fire completionNotice, so
  // they're already handled entirely by the widget's duplicate dialog.
  useEffect(() => {
    if (!completionNotice) return;

    // ── Classify settled jobs ──────────────────────────────────────────────
    const newPoJobs = jobs.filter(j =>
      !j.dismissed && j.status === 'done' &&
      Array.isArray(j.result_po_ids) && j.result_po_ids.length > 0 &&
      (!j.revision_updates || j.revision_updates.length === 0)
    );

    const revisionJobs = jobs.filter(j =>
      !j.dismissed && j.status === 'done' &&
      Array.isArray(j.revision_updates) && j.revision_updates.length > 0 &&
      Array.isArray(j.result_po_ids) && j.result_po_ids.length > 0
    );

    const mixedDupNewJobs = jobs.filter(j =>
      !j.dismissed && j.status === 'duplicate' &&
      (j.processed_files || 0) > 0 &&
      Array.isArray(j.result_po_ids) && j.result_po_ids.length > 0
    );

    const dedup = (arr) =>
      arr.filter((p, i, a) => a.findIndex(x => x.id === p.id) === i)
         .sort((a, b) => b.id - a.id);

    // POs that go directly to batch audit (new + mixed-dup-new, NOT revisions)
    const auditPos  = dedup([...newPoJobs, ...mixedDupNewJobs].flatMap(j => j.result_po_ids));
    // Revision POs that should stay in the widget; user manually navigates
    const revPos    = dedup(revisionJobs.flatMap(j => j.result_po_ids));

    const newCount  = auditPos.length;
    const revCount  = revPos.length;

    // ── Build banner message ───────────────────────────────────────────────
    let alertMsg, auditLabel, onAudit;

    if (newCount > 0 && revCount === 0) {
      // Only new POs — current behaviour
      alertMsg   = `${newCount} PO${newCount !== 1 ? 's' : ''} processed — opening batch audit…`;
      auditLabel = 'Audit now';
      onAudit    = () => { openBatchAudit(null); setProcessingAlert(null); clearCompletionNotice(); };
    } else if (newCount > 0 && revCount > 0) {
      // Mix: new POs auto-go to audit; revisions stay in widget
      alertMsg   = `${newCount} new PO${newCount !== 1 ? 's' : ''} sent to audit · ${revCount} revision${revCount !== 1 ? 's' : ''} — review in widget`;
      auditLabel = 'Audit now';
      onAudit    = () => { openBatchAudit(null); setProcessingAlert(null); clearCompletionNotice(); };
    } else if (revCount > 0) {
      // Only revisions — no auto-nav; user clicks to review
      alertMsg   = `${revCount} revision${revCount !== 1 ? 's' : ''} processed — review in the upload widget`;
      auditLabel = 'Review revisions';
      onAudit    = () => { openBatchAudit(revPos); setProcessingAlert(null); clearCompletionNotice(); };
    } else {
      // Edge-case: nothing to navigate to (shouldn't normally reach here)
      alertMsg   = 'Processing complete — see upload widget for details';
      auditLabel = null;
      onAudit    = null;
    }

    setProcessingAlert({ message: alertMsg, auditLabel, onAudit });

    // ── Auto-navigate — ONLY for new POs, never for revisions ─────────────
    if (newCount > 0 && !location.pathname.startsWith('/audit')) {
      // Pass the exact list so we go to precisely those POs,
      // not a stale mix of previously-uploaded-but-never-dismissed POs.
      openBatchAudit(auditPos);
    }

    // Auto-dismiss the banner after 10 s
    const timer = setTimeout(() => {
      setProcessingAlert(null);
      clearCompletionNotice();
    }, 10000);

    clearCompletionNotice(); // clear trigger so it doesn't fire twice
    return () => clearTimeout(timer);
  }, [completionNotice]); // eslint-disable-line react-hooks/exhaustive-deps
  const isAudit = location.pathname.startsWith('/audit');

  const effectiveWidth = isMobile ? 0 : sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Sidebar
        open={sidebarOpen}
        isMobile={isMobile}
        onToggle={() => setSidebarOpen((v) => !v)}
        onClose={() => setSidebarOpen(false)}
        toggleTheme={toggleThemeMode}
        currentTheme={theme}
      />

      <Box
        component="main"
        sx={{
          flex: 1,
          ml: { xs: 0, md: `${effectiveWidth}px` },
          transition: 'margin 0.25s cubic-bezier(0.4,0,0.2,1)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* ── Persistent header — BR sync + timestamp + Verify BR ── */}
        <AppHeader
          processingAlert={processingAlert}
          onDismissAlert={() => { setProcessingAlert(null); clearCompletionNotice(); }}
        />

        <Box
          sx={{
            flex: 1,
            pt: { xs: isMobile ? 2 : 0, md: 0 },
            p: isAudit
              ? { xs: 2, sm: 2.5 }
              : { xs: 2, sm: 3, md: 4 },
            maxWidth: isAudit ? 'none' : '1400px',
            width: '100%',
            mx: isAudit ? 0 : 'auto',
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Floating upload progress widget — visible on every page */}
      <UploadProgressWidget />

      {/* Persistent upload notifications — survive page navigation */}
      {toasts.map((t, i) => (
        <Snackbar
          key={t.id}
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ bottom: `${24 + i * 72}px !important` }}
        >
          <Alert
            severity={t.severity}
            onClose={() => dismissToast(t.id)}
            action={
              <IconButton size="small" color="inherit" onClick={() => dismissToast(t.id)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ maxWidth: 520 }}
          >
            {t.msg}
          </Alert>
        </Snackbar>
      ))}
    </Box>
  );
}

function AppLayout() {
  return (
    <BRProvider>
      <UploadProvider>
        <AppLayoutInner />
      </UploadProvider>
    </BRProvider>
  );
}

function AuthRoot() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <AuthRoot />,
    children: [
      { path: '/auth/login', element: <LoginPage /> },
      { path: '/auth/callback', element: <AuthCallback /> },
      { path: '/auth/error', element: <AuthError /> },
      {
        element: (
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        ),
        children: [
          { path: '/', element: <FileUpload /> },
          { path: '/dashboard', element: <Dashboard /> },
          { path: '/audit/:poId', element: <AuditPage /> },
          { path: '/document/:documentId', element: <DocumentViewer /> },
          { path: '/navigation', element: <LineItemFilterPage /> },
          { path: '/groups', element: <GroupsPage /> },
          { path: '/revisions', element: <RevisionHistoryPage /> },
          { path: '/users', element: <RequireAdmin><ManageUsersPage /></RequireAdmin> },
        ],
      },
    ],
  },
]);

function App() {
  const [mode, setMode] = useState(() => localStorage.getItem('themeMode') || 'light');

  const colorMode = useMemo(
    () => ({
      theme: mode,
      toggleThemeMode: () => {
        setMode((prev) => {
          const next = prev === 'light' ? 'dark' : 'light';
          localStorage.setItem('themeMode', next);
          return next;
        });
      },
    }),
    [mode],
  );

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  React.useEffect(() => {
    document.documentElement.style.setProperty('--bg-color', theme.palette.background.default);
    document.documentElement.style.setProperty('--text-color', theme.palette.text.primary);
    document.body.style.backgroundColor = theme.palette.background.default;
  }, [theme]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <RouterProvider router={router} />
        </ThemeProvider>
      </ThemeModeContext.Provider>
    </QueryClientProvider>
  );
}

export default App;
